import { useEffect, useMemo, useRef, useState } from 'react';
import type { VehiclePosition } from '../..';
import { ILine } from '../../../../../models/line.model';
import { TransportMode } from '../../../../../models/transport-mode.enum';
import { t } from '../../../../../services/texts.service';
import { IJourneyPrognosis } from '../../models/journey-prognosis.model';
import { JourneyPrognosisApiModel } from '../../models/journey-prognosis.api-model';
import { JourneyPanelHelper } from '../../helpers/journey-panel.helper';
import './style.scss';
import LineBadge from '../../../../Shared/LineBadge';
import { Icon } from '../../../../Shared/Icon';
import { IconMotif } from '../../../../Shared/Icon/icon-motif.enum';
import { IconColor } from '../../../../Shared/Icon/icon-color.enum';
import { IconVariant } from '../../../../Shared/Icon/icon-variant.enum';
import { TransportModeHelper } from '../../../../../helpers/transport-mode.helper';
import { DateTimeHelper } from '../../../../../helpers/date-time.helper';
import { JourneyStreamHelper } from '../../helpers/journey-stream.helper';
import LogService from '../../../../../services/log-service/log.service';
import closeIcon from '../../../../../../assets/icons/xmark-default-blue-1000.svg';
import caretDownIcon from '../../../../../../assets/icons/caret-down-default-blue-1000.svg';
import caretUpIcon from '../../../../../../assets/icons/caret-up-default-blue-1000.svg';
import DataUrlHelper from '../../../../../helpers/data-url.helper';
import type { IStopArea } from '../../../../../models/stop-area.model';
import RealTimeAndPlannedPlatforms from '../../../../TravelPlanner/components/JourneyDetails/components/Details/components/shared/RealTimeAndPlannedPlatforms';
import CallMarker from './components/CallMarker';

export default function JourneyPanel({
  serviceJourneyGid,
  streamUrl,
  line,
  vehicle,
  now,
  destination,
  stopAreas,
  onPrognosisUpdate,
  onClose,
}: {
  serviceJourneyGid: string;
  streamUrl: string;
  line: ILine;
  vehicle?: VehiclePosition;
  now: number;
  destination: string;
  stopAreas: IStopArea[];
  onPrognosisUpdate: () => void;
  onClose: () => void;
}) {
  // === STATE ===
  const [journeyPrognosis, setJourneyPrognosis] = useState<IJourneyPrognosis | undefined>();
  const [isPrognosisLive, setIsPrognosisLive] = useState(false);
  const [isStopListExpanded, setIsStopListExpanded] = useState(false);
  const [lastKnownPosition, setLastKnownPosition] = useState<{ serviceJourneyGid: string; lastStopPointGid: string; atStop: boolean | null } | undefined>();

  // === REFS ===
  const fallbackJourneyPrognosisRef = useRef<IJourneyPrognosis | undefined>(undefined);
  const isTrainJourney = line.transportMode === TransportMode.train;

  // === MEMOIZED VALUES ===
  const stopPointsByGid = useMemo(() => new Map(stopAreas.flatMap((stopArea) => stopArea.stopPoints).map((stopPoint) => [stopPoint.gid, stopPoint])), [stopAreas]);
  const isTimetableOnly = !isTrainJourney && isPrognosisLive && !!journeyPrognosis?.timetableOnly;
  // NV-9111: the vehicle disappears from the stream when it leaves the visible map area, so its last known
  // stop on this journey is kept rather than letting a timetable-only stop list fall back to the clock.
  if (
    vehicle?.lastStopPointGid &&
    vehicle.serviceJourneyGid === serviceJourneyGid &&
    (lastKnownPosition?.serviceJourneyGid !== serviceJourneyGid || lastKnownPosition.lastStopPointGid !== vehicle.lastStopPointGid || lastKnownPosition.atStop !== vehicle.atStop)
  ) {
    setLastKnownPosition({ serviceJourneyGid, lastStopPointGid: vehicle.lastStopPointGid, atStop: vehicle.atStop });
  }
  const knownPosition = lastKnownPosition?.serviceJourneyGid === serviceJourneyGid ? lastKnownPosition : undefined;
  // NV-9135: a real-time journey only follows a position the vehicle is reporting right now, so the producer's
  // progression takes over while the vehicle is outside the visible map area.
  const progressPosition = isTimetableOnly ? knownPosition : vehicle?.serviceJourneyGid === serviceJourneyGid ? vehicle : undefined;
  const vehicleLastStopPointGid = progressPosition?.lastStopPointGid ?? null;
  const vehicleAtStop = progressPosition?.atStop ?? null;
  const displayedPrognosis = useMemo(
    () =>
      journeyPrognosis && isPrognosisLive && !isTrainJourney
        ? { ...journeyPrognosis, calls: JourneyPanelHelper.withPositionProgress(journeyPrognosis.calls, { lastStopPointGid: vehicleLastStopPointGid, atStop: vehicleAtStop }, isTimetableOnly) }
        : journeyPrognosis,
    [journeyPrognosis, isPrognosisLive, isTrainJourney, isTimetableOnly, vehicleLastStopPointGid, vehicleAtStop],
  );
  const remainingJourneyCalls = useMemo(
    () => (displayedPrognosis ? JourneyPanelHelper.getRemainingCalls(displayedPrognosis.calls, isTrainJourney) : []),
    [displayedPrognosis, isTrainJourney],
  );

  const [stopListTransition, setStopListTransition] = useState({ source: remainingJourneyCalls, calls: remainingJourneyCalls });
  if (stopListTransition.source !== remainingJourneyCalls) {
    setStopListTransition({ source: remainingJourneyCalls, calls: JourneyPanelHelper.getTransitionCalls(stopListTransition.calls, remainingJourneyCalls) });
  }
  const hasExitingStopRows = stopListTransition.calls.length > remainingJourneyCalls.length;

  // === EFFECTS ===
  useEffect(() => {
    // Remove departing rows after the transition, immediately when reduced motion is preferred.
    if (!hasExitingStopRows) return;
    const timeout = setTimeout(() => setStopListTransition({ source: remainingJourneyCalls, calls: remainingJourneyCalls }), window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300);
    return () => clearTimeout(timeout);
  }, [hasExitingStopRows, remainingJourneyCalls]);

  useEffect(() => {
    // Retain confirmed progression, not the scheduled clock, when the stream becomes unavailable.
    fallbackJourneyPrognosisRef.current = displayedPrognosis
      ? { ...displayedPrognosis, calls: displayedPrognosis.calls.map((call) => ({ ...call, passed: !remainingJourneyCalls.includes(call) })) }
      : undefined;
  }, [displayedPrognosis, remainingJourneyCalls]);

  useEffect(() => {
    // NV-9098: Temporarily hide train timetables/prognoses until Trafikverket data is complete.
    // Remove this guard and the train-specific status text below when the data is reliable.
    if (isTrainJourney) return;

    // Subscribe to the selected journey and fall back to received scheduled times when live data is unavailable.
    const fallback = () => {
      onPrognosisUpdate();
      setIsPrognosisLive(false);
      setJourneyPrognosis(fallbackJourneyPrognosisRef.current ? JourneyPanelHelper.timetableOnly(fallbackJourneyPrognosisRef.current) : undefined);
    };
    return JourneyStreamHelper.subscribeToJourneyPrognosis(
      streamUrl,
      serviceJourneyGid,
      (data) => {
        try {
          const model = JourneyPrognosisApiModel.mapToBusinessModel(JSON.parse(data));
          if (model.serviceJourneyGid !== serviceJourneyGid) return;
          if (!model.calls.some((call) => (isTrainJourney ? !!call.locationSignature : !!call.stopPointGid))) {
            fallback();
            return;
          }
          fallbackJourneyPrognosisRef.current = model;
          onPrognosisUpdate();
          setJourneyPrognosis(model);
          setIsPrognosisLive(true);
        } catch (error) {
          LogService.logError(error instanceof Error ? error : new Error('Invalid journey prognosis snapshot'));
          fallback();
        }
      },
      fallback,
    );
  }, [serviceJourneyGid, streamUrl, isTrainJourney, onPrognosisUpdate]);

  // === VARIABLES ===
  const hasArrivals = JourneyPanelHelper.hasArrivalTimes(remainingJourneyCalls, isPrognosisLive);
  const hasRealtime = isPrognosisLive && remainingJourneyCalls.some((call) => {
    const times = JourneyPanelHelper.getCallTimes(call);
    return !!(times.actual ?? times.estimated);
  });
  const hasLiveTrainPrognosis = isTrainJourney && isPrognosisLive;
  const timetableWarning = hasArrivals && !hasRealtime && !hasLiveTrainPrognosis && !(isPrognosisLive && journeyPrognosis?.cancelled);
  let status = '';
  switch (true) {
    case journeyPrognosis?.cancelled && isPrognosisLive:
      status = t('Turen är inställd.', 'This journey is cancelled.');
      break;
    case !journeyPrognosis:
      // NV-9098: Temporarily hide train timetables/prognoses until Trafikverket data is complete.
      // Remove this guard and the train-specific status text below when the data is reliable.
      status = isTrainJourney
        ? t('Vi har inga tidtabeller för detta fordon.', 'We have no timetables for this vehicle.')
        : t('Information om kommande hållplatser saknas.', 'Upcoming stop information is unavailable.');
      break;
    case !hasArrivals || remainingJourneyCalls.length === 0:
      status = t('Information om kommande hållplatser saknas.', 'Upcoming stop information is unavailable.');
      break;
    case timetableWarning:
      status = t('Just nu visas endast tidtabellstider', 'Only scheduled times are currently shown');
      break;
  }

  return (
    <section className={`journey-panel d-flex flex-column bg-white pb-3 last-child-margin-bottom-none ${isStopListExpanded ? 'journey-panel--expanded' : ''}`} aria-labelledby="journey-panel-heading">
      <div className="d-flex align-items-center gap-2 px-3 pt-3 mb-1 flex-shrink-0">
        <h2 id="journey-panel-heading" className="journey-panel__title mb-0 flex-grow-1 d-flex align-items-center gap-1">
          <LineBadge line={line} />
          <Icon {...TransportModeHelper.getIcon(line.transportMode)} className="d-block flex-shrink-0" />
          <span className="journey-panel__destination">{destination || line.name}</span>
        </h2>
        <button type="button" className="btn btn-icon-only flex-shrink-0" aria-label={t.close} onClick={onClose}>
          <img src={DataUrlHelper.encode(closeIcon)} alt="" />
        </button>
      </div>
      <p className={`px-3 mb-0 d-flex align-items-center gap-1 flex-shrink-0 ${timetableWarning ? 'text-danger' : ''}`} role="status" aria-live="polite">
        {timetableWarning && <Icon motif={IconMotif.Exclamation} color={IconColor.Red600} variant={IconVariant.Circle} altText="" className="flex-shrink-0" />}
        {status}
      </p>
      <span className="visually-hidden" role="status" aria-live="polite">
        {remainingJourneyCalls[0] && !journeyPrognosis?.cancelled
          ? `${t('Första hållplats i listan', 'First stop in the list')}: ${remainingJourneyCalls[0].stopAreaName || t('Hållplatsnamn saknas', 'Stop name unavailable')}`
          : ''}
      </span>
      {!(isPrognosisLive && journeyPrognosis?.cancelled) && hasArrivals && (
        <ol
          id="journey-panel-stops"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          aria-label={t('Kommande hållplatser', 'Upcoming stops')}
          className={`journey-panel__stops list-unstyled mb-1 pe-3 ${displayedPrognosis && JourneyPanelHelper.hasPreviousCalls(displayedPrognosis.calls, remainingJourneyCalls, isTrainJourney) ? 'journey-panel__stops--has-previous' : ''}`}
        >
          {stopListTransition.calls.map((call, index) => {
            const cancelled = isPrognosisLive && call.cancelled;
            const arrival = JourneyPanelHelper.getArrival(call, hasRealtime && !hasLiveTrainPrognosis && !cancelled);
            const isLeavingStopList = !remainingJourneyCalls.includes(call);
            const visibleIndex = isLeavingStopList ? index : remainingJourneyCalls.indexOf(call);
            const entering = !isLeavingStopList && hasExitingStopRows && index > 1 && visibleIndex <= 1;
            const hasLaterStops = !isLeavingStopList && visibleIndex === 1 && remainingJourneyCalls.length > 2;
            const isAtStop = call === remainingJourneyCalls[0] && !isTrainJourney && isPrognosisLive && !cancelled && JourneyPanelHelper.isVehicleAtCall(call, vehicle, now);
            return (
              <li
                key={`${call.order}-${call.stopPointGid ?? call.locationSignature}`}
                aria-hidden={isLeavingStopList || undefined}
                className={`journey-panel__row ${isLeavingStopList ? 'journey-panel__row--leaving' : ''} ${entering ? 'journey-panel__row--entering' : ''} ${visibleIndex > 1 ? 'journey-panel__stop--later' : ''}`}
              >
                <div className="journey-panel__row-content">
                  <div className={`journey-panel__stop position-relative px-3 pb-2 ${hasLaterStops ? 'journey-panel__stop--has-later' : ''}`}>
                    <CallMarker isAtStop={isAtStop} line={line} />
                    <div className={`journey-panel__arrival ${cancelled || !arrival.time ? 'text-danger' : ''}`}>
                      <span className={cancelled && arrival.time ? 'text-decoration-line-through' : ''}>
                        {arrival.time ? (
                          <>
                            <time dateTime={arrival.time.toISO() ?? undefined}>{DateTimeHelper.toTime(arrival.time)}</time>{' '}
                          </>
                        ) : (
                          t('Ankomsttid saknas', 'Arrival time unavailable')
                        )}
                        {arrival.planned && (
                          <>
                            <del>
                              <time dateTime={arrival.planned.toISO() ?? undefined}>{DateTimeHelper.toTime(arrival.planned)}</time>
                            </del>
                          </>
                        )}
                      </span>
                      {cancelled && <span className="ms-1">{t('Inställd', 'Cancelled')}</span>}
                    </div>
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <strong className="journey-panel__stop-name d-block">{call.stopAreaName || t('Hållplatsnamn saknas', 'Stop name unavailable')}</strong>
                      <RealTimeAndPlannedPlatforms stopPoint={call.stopPointGid ? stopPointsByGid.get(call.stopPointGid) : undefined} realtimeStopPoint={undefined} className="text-muted" />
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {hasArrivals && remainingJourneyCalls.length > 2 && !(isPrognosisLive && journeyPrognosis?.cancelled) && (
        <button
          type="button"
          className="journey-panel__toggle-stops btn btn-tertiary d-inline-flex align-items-center gap-1 d-lg-none me-3 mb-2 py-1 align-self-start"
          aria-expanded={isStopListExpanded}
          aria-controls="journey-panel-stops"
          onClick={() => setIsStopListExpanded(!isStopListExpanded)}
        >
          <span>{isStopListExpanded ? t('Visa färre hållplatser', 'Show fewer stops') : t('Senare hållplatser', 'Later stops')}</span>
          <img src={DataUrlHelper.encode(isStopListExpanded ? caretUpIcon : caretDownIcon)} alt="" className="d-block flex-shrink-0" />
        </button>
      )}
    </section>
  );
}
